<script setup lang="ts">
import {
  HIGH_ASSOCIATION_THRESHOLD,
  type TableReportDto,
  tableReportHasHighAssociation,
} from "@/components/datascience/TableReport.vue";
import { smartToFixed } from "@/services/utils";

const { spec } = defineProps<{
  spec: TableReportDto;
}>();
</script>

<template>
  <div class="associations">
    <table>
      <thead>
        <tr>
          <th>Column 1</th>
          <th>Column 2</th>
          <th :class="{ warning: tableReportHasHighAssociation(spec) }">Cramer's V</th>
          <th>Pearson correlation</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(association, i) in spec.top_associations" :key="i">
          <td>{{ association.left_column_name }}</td>
          <td>{{ association.right_column_name }}</td>
          <td
            :class="{ warning: association.cramer_v >= HIGH_ASSOCIATION_THRESHOLD }"
            class="number"
          >
            <span v-if="association.cramer_v > 0">&numsp;</span>
            {{ smartToFixed(association.cramer_v) }}
          </td>
          <td
            v-if="association.pearson_corr && Number.isFinite(association.pearson_corr)"
            class="number"
          >
            <span v-if="association.pearson_corr > 0">&numsp;</span>
            {{ smartToFixed(association.pearson_corr) }}
          </td>
          <td v-else class="disabled">&numsp;—</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped>
.associations {
  table {
    width: 100%;
    max-width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    table-layout: fixed;

    &,
    &:focus,
    &:focus-visible {
      outline: none;
    }

    thead {
      tr {
        color: var(--content-secondary);

        th {
          z-index: 1;
          padding: var(--numbers-8);
          border-width: var(--stroke-width-md);
          border-color: var(--border-tertiary);
          border-top-style: solid;
          background-color: var(--background-tertiary);
          text-align: left;
        }

        &:last-child th {
          border-bottom-style: solid;
        }

        &:first-child th:first-child {
          border-left-style: solid;
          border-top-left-radius: var(--numbers-8);
        }

        &:first-child th:last-child {
          border-right-style: solid;
          border-top-right-radius: var(--numbers-4);
        }
      }
    }

    tbody {
      tr {
        td {
          overflow: hidden;
          max-width: 25%;
          padding: var(--numbers-8);
          border: var(--stroke-width-md) none var(--border-tertiary);
          border-bottom-style: solid;
          border-left-style: solid;
          text-align: left;
          text-overflow: ellipsis;
          transition: color var(--animation-duration) var(--animation-easing);
          white-space: nowrap;

          &.number {
            white-space: pre;
          }

          &:last-child {
            border-right-style: solid;
          }

          &.disabled {
            color: var(--content-disabled);
          }
        }

        &:last-child td {
          border-bottom-style: solid;
        }

        &:last-child td:first-child {
          border-bottom-left-radius: var(--numbers-8);
        }

        &:last-child td:last-child {
          border-bottom-right-radius: var(--numbers-8);
        }
      }
    }

    th.warning,
    td.warning {
      color: var(--content-negative-bold);
    }
  }
}
</style>
